import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Starfield } from "../components/Starfield";
import { useAuth } from "../context/AuthContext";

const numbers = [
  { n: 1, ruler: "Sun", trait: "Leadership" },
  { n: 2, ruler: "Moon", trait: "Intuition" },
  { n: 3, ruler: "Jupiter", trait: "Expansion" },
  { n: 4, ruler: "Rahu", trait: "Discipline" },
  { n: 5, ruler: "Mercury", trait: "Charisma" },
  { n: 6, ruler: "Venus", trait: "Beauty" },
  { n: 7, ruler: "Ketu", trait: "Mystery" },
  { n: 8, ruler: "Saturn", trait: "Karma" },
];

const Landing = () => {
  const { user } = useAuth();

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleLogin = () => {
    const redirectUrl = window.location.origin + "/ask";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="relative min-h-screen">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1475274047050-1d0c0975c63e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHw0fHxteXN0aWNhbCUyMG5pZ2h0JTIwc2t5JTIwc3RhcnMlMjBnYWxheHl8ZW58MHx8fHwxNzg1NTY4MTE4fDA&ixlib=rb-4.1.0&q=85"
            alt=""
            className="h-full w-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/50 to-[#0A0A0C]" />
          <div className="absolute inset-0 bg-noise opacity-40" />
          <Starfield count={80} />
        </div>

        <div className="relative mx-auto max-w-7xl px-6 pt-20 pb-24 sm:pt-32 sm:pb-40">
          <div className="max-w-3xl fade-up">
            <span className="inline-block text-[10px] sm:text-xs uppercase tracking-[0.3em] text-gold border border-gold/30 rounded-full px-3 sm:px-4 py-1.5 mb-6 sm:mb-8" data-testid="hero-tagline">
              Numerology · Since Antiquity
            </span>
            <h1 className="font-serif text-4xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter mb-5 sm:mb-6" data-testid="hero-heading">
              Ask the numbers <br />
              <span className="italic text-gold">that made the world.</span>
            </h1>
            <p className="text-base sm:text-lg text-neutral-300 max-w-xl leading-relaxed mb-8 sm:mb-10">
              Every letter carries a vibration — 1 through 8, never 9. Numero reads yours and answers
              the questions that matter, in the ancient key.
            </p>
            <div className="flex flex-wrap items-center gap-3 sm:gap-4">
              {user ? (
                <Link to="/ask">
                  <Button
                    data-testid="cta-ask"
                    className="rounded-full bg-gold text-black hover:bg-[#F3E5AB] px-6 sm:px-8 py-5 sm:py-6 text-sm sm:text-base transition-colors duration-200"
                  >
                    Ask a question
                  </Button>
                </Link>
              ) : (
                <Button
                  onClick={handleLogin}
                  data-testid="cta-signin"
                  className="rounded-full bg-gold text-black hover:bg-[#F3E5AB] px-6 sm:px-8 py-5 sm:py-6 text-sm sm:text-base transition-colors duration-200"
                >
                  Sign in — 1 free credit
                </Button>
              )}
              <Link to="/pricing">
                <Button
                  variant="outline"
                  data-testid="cta-pricing"
                  className="rounded-full border-white/20 text-white bg-transparent hover:bg-white/5 hover:text-white px-6 sm:px-8 py-5 sm:py-6 text-sm sm:text-base transition-colors duration-200"
                >
                  See credit packages
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Number map bento */}
      <section className="relative mx-auto max-w-7xl px-4 sm:px-6 py-16 sm:py-24">
        <div className="grid gap-5 sm:gap-6 md:grid-cols-12">
          <div className="md:col-span-7 rounded-2xl border border-white/5 bg-[#141419] p-6 sm:p-10">
            <p className="text-xs uppercase tracking-[0.25em] text-neutral-500 mb-3 sm:mb-4">The 8 Vibrations</p>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight mb-5 sm:mb-6">
              Every letter carries a planet.
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
              {numbers.map((n) => (
                <div
                  key={n.n}
                  className="rounded-xl border border-white/5 bg-black/40 p-3 sm:p-4 hover:border-gold/40 transition-colors duration-200"
                >
                  <div className="font-serif text-2xl sm:text-3xl text-gold">{n.n}</div>
                  <div className="text-sm text-neutral-200 mt-1">{n.ruler}</div>
                  <div className="text-xs text-neutral-500">{n.trait}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="md:col-span-5 rounded-2xl border border-white/5 bg-[#141419] p-6 sm:p-10 flex flex-col justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-neutral-500 mb-3 sm:mb-4">How it works</p>
              <ol className="space-y-4 sm:space-y-5 text-neutral-300 text-sm sm:text-base">
                <li className="flex gap-4">
                  <span className="font-serif text-gold text-2xl">1</span>
                  <span>Enter your full name and date of birth — the foundations of your reading.</span>
                </li>
                <li className="flex gap-4">
                  <span className="font-serif text-gold text-2xl">2</span>
                  <span>Ask any question — about love, career, timing, or the season ahead.</span>
                </li>
                <li className="flex gap-4">
                  <span className="font-serif text-gold text-2xl">3</span>
                  <span>
                    Go deeper with <Link to="/tools" className="text-gold hover:underline">Tools</Link> —
                    two-person compatibility, name-spelling advisor, and mobile / bank number checks with favourable alternatives.
                  </span>
                </li>
                <li className="flex gap-4">
                  <span className="font-serif text-gold text-2xl">4</span>
                  <span>Credits are deducted by depth — 1 for a lookup, up to 4 for a full analysis.</span>
                </li>
              </ol>
            </div>
            <div className="mt-6 sm:mt-8">
              <Link to="/pricing">
                <Button
                  variant="outline"
                  className="rounded-full border-white/20 bg-transparent hover:bg-white/5 hover:text-white"
                  data-testid="cta-secondary-pricing"
                >
                  Buy credits →
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/5 py-10 text-center text-sm text-neutral-500">
        <span className="font-serif italic">Numero by Auracean</span> · Numerology readings for the modern seeker
      </footer>
    </div>
  );
};

export default Landing;
